/**
 * The question card (display `question_card`, from Clu's `ask_questions`
 * tool, whose events carry the public name `ask_user`; the card is known by
 * its display, never by the tool's name): reading the payload, composing the
 * answers message the backend reads, and
 * working out, from the thread around a card, whether it is still waiting for
 * the user, was answered, or was passed over.
 *
 * The answers go back as the user's next message, in the exact form the
 * backend contract fixes (phase 3b backend spec, section 9.2):
 *
 *   Answers:
 *   1. Which titles count? CRO + Head of Revenue
 *   2. What counts as new? Other: started in the role within 90 days
 *
 * Several picks join with ", "; a written answer gets "Other: " in front.
 * Ordinary question cards send `Skipped the questions.` when rejected.
 * Requirement reviews send `Workstream rejected.` with the typed decision.
 */

import {
  oneTimeWords,
  scheduleWithZone,
} from '@/containers/agents/agent-row.util';
import type { TriggerSchedule } from '@/types/custom-playbooks';
import { asRecord } from '@/components/agent-chat/as-record.util';

export const SKIPPED_QUESTIONS_MESSAGE = 'Skipped the questions.';
export const WORKSTREAM_REJECTED_MESSAGE = 'Workstream rejected.';

// Old requirement-review replies used the ordinary skip text. Keep replay
// compatible while newly rejected workstreams name the actual decision.
function isReviewRejection(text: string): boolean {
  return (
    text.trim() === WORKSTREAM_REJECTED_MESSAGE ||
    text.trim() === SKIPPED_QUESTIONS_MESSAGE
  );
}
const ANSWERS_HEADER = 'Answers:';

export interface QuestionOption {
  key: string;
  label: string;
}

export interface CardQuestion {
  id: string;
  question: string;
  detail: string | null;
  options: QuestionOption[];
  multiple: boolean;
  other: boolean;
}

export interface QuestionCardData {
  cardId: string;
  /** The line Clu wrote to go above the card; null on an older card. */
  leadIn: string | null;
  questions: CardQuestion[];
  requirementReview?: {
    revision: string;
    conversationId: string;
    requirements: { id: string; text: string; source?: string }[];
    /** The asks carried from earlier requests that the build is still held
     * to, shown folded on the card; empty on a card from before them. */
    carried: string[];
    sourceText?: string;
    resolvedSlots: {
      key: string;
      label: string;
      value: string;
      source: string;
    }[];
  };
}

export interface QuestionAnswer {
  /** Keys of the picked options. */
  picked: string[];
  /** The written answer while Other is picked; null while it is not. */
  other: string | null;
}

const LETTERS = 'ABCDEFGHIJ';

/** The letter a row shows: the option's own key, or its place in the list. */
export function optionLetter(index: number): string {
  return LETTERS[index] ?? String(index + 1);
}

function oneLine(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function parseOptions(raw: unknown): QuestionOption[] {
  if (!Array.isArray(raw)) return [];
  const options: QuestionOption[] = [];
  raw.forEach((item, index) => {
    const record = asRecord(item);
    const label = record ? oneLine(record.label) : oneLine(item);
    if (!label) return;
    const key = record ? oneLine(record.key) : '';
    options.push({ key: key || optionLetter(index), label });
  });
  return options;
}

/**
 * True for an option that is itself the card's Other ("Other", "Other:",
 * "Other (please specify)"): Clu sometimes lists one while the card already
 * offers its own Other, which turns into a text field.
 */
function isOtherLabel(label: string): boolean {
  return (
    label
      .replace(/\(.*?\)/g, '')
      .replace(/[^a-z ]/gi, '')
      .trim()
      .toLowerCase() === 'other'
  );
}

function parseQuestions(raw: unknown): CardQuestion[] {
  if (!Array.isArray(raw)) return [];
  const questions: CardQuestion[] = [];
  raw.forEach((item, index) => {
    const record = asRecord(item);
    if (!record) return;
    const question = oneLine(record.question);
    if (!question) return;
    const detail = oneLine(record.detail);
    // The backend's default is true: the user may write their own.
    const other = record.other !== false;
    const options = parseOptions(record.options).filter(
      (option) => !other || !isOtherLabel(option.label)
    );
    questions.push({
      id: oneLine(record.id) || `q${index + 1}`,
      question,
      detail: detail || null,
      options,
      multiple: record.multiple === true,
      other,
    });
  });
  return questions;
}

/**
 * The card as the dock renders it, from the tool result's display payload
 * (`card_id`, `lead_in`, `questions[]` with keyed options), falling back to
 * the validated `questions` and `lead_in` the result itself carries. Null
 * when neither holds a usable question.
 */
export function parseQuestionCard(
  display: unknown,
  result?: unknown
): QuestionCardData | null {
  const shown = asRecord(display);
  const fromDisplay = parseQuestions(shown?.questions);
  const questions =
    fromDisplay.length > 0
      ? fromDisplay
      : parseQuestions(asRecord(result)?.questions);
  const rawReview = asRecord(shown?.requirement_review);
  let requirementReview: QuestionCardData['requirementReview'];
  if (rawReview) {
    const revision = oneLine(rawReview.revision);
    const conversationId = oneLine(rawReview.conversation_id);
    const requirements = Array.isArray(rawReview.requirements)
      ? rawReview.requirements.map(asRecord)
      : [];
    const carried = rawReview.carried ?? [];
    if (
      !Array.isArray(carried) ||
      carried.some((line) => typeof line !== 'string' || !line.trim())
    )
      return null;
    if (
      !oneLine(shown?.card_id) ||
      !revision ||
      !conversationId ||
      !requirements.length ||
      requirements.some((item) => !oneLine(item?.id) || !oneLine(item?.text)) ||
      new Set(requirements.map((item) => oneLine(item?.id))).size !==
        requirements.length ||
      !Array.isArray(shown?.questions) ||
      shown.questions.length !== questions.length ||
      // A build asks up to seven (api gtm_agents.MAX_BUILD_QUESTIONS).
      questions.length > 7 ||
      new Set(questions.map((item) => item.id)).size !== questions.length
    )
      return null;
    requirementReview = {
      revision,
      conversationId,
      requirements: requirements.map((item) => ({
        id: oneLine(item?.id),
        text: item!.text as string,
        source: oneLine(item?.source) || undefined,
      })),
      carried: carried as string[],
      sourceText:
        typeof rawReview.source_text === 'string'
          ? rawReview.source_text
          : undefined,
      resolvedSlots: (Array.isArray(rawReview.resolved_slots)
        ? rawReview.resolved_slots
        : []
      )
        .map(asRecord)
        .filter(
          (item) =>
            oneLine(item?.key) &&
            oneLine(item?.label) &&
            oneLine(item?.value) &&
            oneLine(item?.source)
        )
        .map((item) => ({
          key: oneLine(item?.key),
          label: oneLine(item?.label),
          value: oneLine(item?.value),
          source: oneLine(item?.source),
        })),
    };
  }
  // An invalid review must not degrade to an ordinary, unbound answer card.
  if (shown?.requirement_review !== undefined && !requirementReview)
    return null;
  if (questions.length === 0 && !requirementReview) return null;
  return {
    cardId: oneLine(shown?.card_id) || questions.map((q) => q.id).join('|'),
    leadIn:
      oneLine(shown?.lead_in) || oneLine(asRecord(result)?.lead_in) || null,
    questions,
    ...(requirementReview ? { requirementReview } : {}),
  };
}

type ResolvedSlot = NonNullable<
  QuestionCardData['requirementReview']
>['resolvedSlots'][number];

// The API's raw slot values (cleanse-api `build_review.resolved_slots`), by
// slot key. A value not listed here, such as a card answer's own label or a
// record type, already reads as words.
const SLOT_VALUE_WORDS: Record<string, Record<string, string>> = {
  crm_object: { default: 'Your CRM settings’ record type' },
  // A saved Sync's target. "record" matches and updates, or creates when no
  // record matches. Account and deal are another CRM action's target, on
  // cards made before the API read only a Sync's.
  crm_write_mode: {
    create_only: 'Create only',
    update_only: 'Update only',
    record: 'Create or update the record',
    new_record: 'Create a new record',
    account: 'Update the account',
    deal: 'Update the deal',
  },
  notify_recipient: {
    play_owner: 'You (the agent owner)',
    record_owner: 'Each record’s owner',
    default: 'The default recipients',
  },
  notify_destination: {
    slack_dm: 'Slack DM',
    slack_channel: 'Slack channel',
    email: 'Email',
  },
  notify_delivery: {
    summary: 'One message per run',
    list: 'One list per run',
    per_record: 'One message per record',
  },
  notify_fallback: { default: 'The default recipients', skip: 'Skip it' },
  // A choice from the user's words, or a saved Notify's ownerField. Any
  // other field passes through as its path.
  owner_source: {
    old_contact: 'The old contact’s owner',
    account: 'The account owner',
    record: 'The record owner',
    'crm.owner_id': 'The record owner',
    'crm.account.owner_id': 'The account owner',
    'crm.deal.owner_id': 'The deal owner',
    'crm.original.owner_id': 'The old contact’s owner',
    'crm.original.account.owner_id': 'The old account’s owner',
    'trigger.owner_id': 'The triggering record’s owner',
  },
  sender: {
    sequence_default: 'The sequence’s default sender',
    record_owner: 'The record owner',
    user: 'A chosen user',
  },
};

const SLOT_SOURCE_WORDS: Record<string, string> = {
  'user request': 'You asked',
  'user card answer': 'Your answer',
  'saved play': 'From the saved play',
  'salesforce settings': 'Your Salesforce settings',
  'hubspot settings': 'Your HubSpot settings',
  'attio settings': 'Your Attio settings',
};

/**
 * A dict value the API wrote with Python's `str()` ("{'time': '09:00',
 * 'weekday': 0}") or as JSON; null for anything else.
 */
// ponytail: quote swap, fine for schedule/sender dicts (enums, times, zone
// names); have the API send JSON if a value can ever hold a quote.
function dictValue(value: string): Record<string, unknown> | null {
  if (!value.startsWith('{')) return null;
  try {
    return asRecord(
      JSON.parse(
        value
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
          .replace(/\bNone\b/g, 'null')
      )
    );
  } catch {
    return null;
  }
}

/**
 * A resolved slot as the review card shows it: its value and where it came
 * from in words ("Weekly, Mon 9:00 AM, Toronto time", "From the saved play"),
 * never the API's raw value and source.
 */
export function slotWords(slot: ResolvedSlot): {
  value: string;
  source: string;
} {
  const dict = dictValue(slot.value);
  const schedule =
    slot.key === 'schedule' && dict && (dict as unknown as TriggerSchedule);
  const value = schedule
    ? schedule.frequency === 'once'
      ? oneTimeWords(schedule)
      : scheduleWithZone(schedule)
    : SLOT_VALUE_WORDS[slot.key]?.[
        typeof dict?.mode === 'string' ? dict.mode : slot.value
      ];
  return {
    value: value || slot.value,
    source: SLOT_SOURCE_WORDS[slot.source] ?? slot.source,
  };
}

export function emptyAnswer(): QuestionAnswer {
  return { picked: [], other: null };
}

/** A question counts as answered once something is picked and Other, when picked, is written. */
export function isQuestionAnswered(
  answer: QuestionAnswer | undefined
): boolean {
  if (!answer) return false;
  if (answer.other !== null && !answer.other.trim()) return false;
  return answer.picked.length > 0 || answer.other !== null;
}

/** One question's answer as the message states it. */
export function answerText(
  question: CardQuestion,
  answer: QuestionAnswer
): string {
  const parts = question.options
    .filter((option) => answer.picked.includes(option.key))
    .map((option) => option.label);
  if (answer.other !== null) {
    const written = oneLine(answer.other);
    if (written) parts.push(`Other: ${written}`);
  }
  return parts.join(', ');
}

/** The user's answers message, exactly in the backend contract's format. */
export function composeAnswersMessage(
  card: QuestionCardData,
  answers: QuestionAnswer[]
): string {
  const lines = card.questions.map(
    (question, index) =>
      `${index + 1}. ${question.question} ${answerText(
        question,
        answers[index] ?? emptyAnswer()
      )}`
  );
  return [
    ANSWERS_HEADER,
    ...lines,
    ...(card.requirementReview ? ['Workstream confirmed.'] : []),
  ].join('\n');
}

/**
 * When `text` is this card's answers message (or the skip message), each
 * question's answer as it was sent ("Skipped" for the skip message); null
 * when the text is something else the user wrote.
 */
export function readAnswersMessage(
  card: QuestionCardData,
  text: string
): string[] | null {
  const trimmed = text.trim();
  if (
    trimmed === SKIPPED_QUESTIONS_MESSAGE ||
    (card.requirementReview && isReviewRejection(text))
  ) {
    return card.questions.map(() => 'Skipped');
  }
  const lines = trimmed.split('\n').map((line) => line.trim());
  if (lines[0] !== ANSWERS_HEADER) return null;
  const numbered = lines.slice(1).filter(Boolean);
  if (card.requirementReview && numbered.pop() !== 'Workstream confirmed.')
    return null;
  if (numbered.length !== card.questions.length) return null;
  const answers: string[] = [];
  for (let index = 0; index < card.questions.length; index += 1) {
    const prefix = `${index + 1}. `;
    const line = numbered[index];
    if (!line.startsWith(prefix)) return null;
    const rest = line.slice(prefix.length);
    const question = card.questions[index].question;
    answers.push(
      rest.startsWith(question) ? rest.slice(question.length).trim() : rest
    );
  }
  return answers;
}

// ---------------------------------------------------------------------------
// The card in its thread
// ---------------------------------------------------------------------------

/** What this reads of an assistant-ui thread message and its parts. */
export interface ThreadPartLike {
  readonly type: string;
  readonly toolCallId?: string;
  readonly toolName?: string;
  readonly args?: unknown;
  readonly result?: unknown;
  readonly text?: string;
}

export interface ThreadMessageLike {
  readonly metadata?: { readonly custom?: Readonly<Record<string, unknown>> };
  readonly id: string;
  readonly role: string;
  readonly content: readonly ThreadPartLike[];
}

export const QUESTION_CARD_DISPLAY = 'question_card';

/** The display type a converted tool part carries (see `toToolResultPart`). */
export function partDisplayType(part: ThreadPartLike): string | undefined {
  if (part.type !== 'tool-call') return undefined;
  const args = asRecord(part.args);
  if (typeof args?.displayType === 'string') return args.displayType;
  const result = asRecord(part.result);
  return typeof result?.__displayType === 'string'
    ? result.__displayType
    : undefined;
}

/** The question card a part carries, or null for any other part. */
export function questionCardOfPart(
  part: ThreadPartLike
): QuestionCardData | null {
  if (partDisplayType(part) !== QUESTION_CARD_DISPLAY) return null;
  const result = asRecord(part.result);
  return parseQuestionCard(result?.__display, result);
}

export function messageText(message: ThreadMessageLike): string {
  return message.content
    .filter((part) => part.type === 'text')
    .map((part) => part.text ?? '')
    .join('');
}

/** The last question card of a message: the only one that can still be open. */
function lastQuestionCard(message: ThreadMessageLike): {
  toolCallId: string | undefined;
  card: QuestionCardData;
} | null {
  for (let index = message.content.length - 1; index >= 0; index -= 1) {
    const part = message.content[index];
    const card = questionCardOfPart(part);
    if (card) return { toolCallId: part.toolCallId, card };
  }
  return null;
}

/**
 * The text of the first user message after `messageId`, or null while none
 * has been sent. A primitive, so a selector returning it re-renders a card
 * only when that message changes.
 */
export function followingUserText(
  messages: readonly ThreadMessageLike[],
  messageId: string
): string | null {
  const index = messages.findIndex((message) => message.id === messageId);
  if (index < 0) return null;
  for (let next = index + 1; next < messages.length; next += 1) {
    if (messages[next].role === 'user') return messageText(messages[next]);
  }
  return null;
}

export function followingUserInput(
  messages: readonly ThreadMessageLike[],
  messageId: string
): unknown {
  const index = messages.findIndex((message) => message.id === messageId);
  if (index < 0) return undefined;
  return messages.slice(index + 1).find((message) => message.role === 'user')
    ?.metadata?.custom?.userInput;
}

function reviewInputMatches(
  card: QuestionCardData,
  text: string,
  input: unknown
): boolean {
  if (!card.requirementReview) return true;
  const value = asRecord(input);
  return (
    value?.type === 'requirement_review' &&
    value.card_id === card.cardId &&
    value.revision === card.requirementReview.revision &&
    value.decision === (isReviewRejection(text) ? 'rejected' : 'confirmed')
  );
}

/** True when `toolCallId` is the last question card of its message. */
export function isLastQuestionCardOf(
  message: ThreadMessageLike,
  toolCallId: string | undefined
): boolean {
  const last = lastQuestionCard(message);
  return !!last && last.toolCallId === toolCallId;
}

export type QuestionCardState =
  | { kind: 'open' }
  | { kind: 'answered'; lines: string[] }
  | { kind: 'not_answered'; lines: string[] };

/**
 * Where a card stands: open while no user message follows it (and it is the
 * last card of its message); answered when the next user message is its
 * answers or the skip message, with one "Question: answer" line per
 * question; passed over when the user wrote something else instead.
 */
export function questionCardState(
  card: QuestionCardData,
  followingText: string | null,
  isLastCardOfMessage: boolean,
  userInput?: unknown
): QuestionCardState {
  const notAnswered: QuestionCardState = {
    kind: 'not_answered',
    lines: [
      ...(card.requirementReview ? ['Workstream: Not confirmed'] : []),
      ...card.questions.map((q) => `${q.question}: Not answered`),
    ],
  };
  if (followingText === null) {
    return isLastCardOfMessage ? { kind: 'open' } : notAnswered;
  }
  if (!reviewInputMatches(card, followingText, userInput)) return notAnswered;
  const answers = isLastCardOfMessage
    ? readAnswersMessage(card, followingText)
    : null;
  if (!answers) return notAnswered;
  return {
    kind: 'answered',
    lines: [
      ...(card.requirementReview
        ? [
            isReviewRejection(followingText)
              ? 'Workstream: Rejected'
              : 'Workstream: Confirmed',
          ]
        : []),
      ...card.questions.map(
        (q, index) => `${q.question}: ${answers[index] || 'Skipped'}`
      ),
    ],
  };
}

/**
 * True for the user message at `index` when it answers (or skips) the
 * question card at the end of the assistant message right before it. The
 * Gumloop style shows no bubble for it: the card folds into the steps of the
 * message that asked (see folded-messages.util).
 */
export function isAnswersMessageAt(
  messages: readonly ThreadMessageLike[],
  index: number
): boolean {
  if (index <= 0 || index >= messages.length) return false;
  const message = messages[index];
  const previous = messages[index - 1];
  if (message.role !== 'user' || previous.role !== 'assistant') return false;
  const last = lastQuestionCard(previous);
  if (!last) return false;
  return (
    reviewInputMatches(
      last.card,
      messageText(message),
      message.metadata?.custom?.userInput
    ) && readAnswersMessage(last.card, messageText(message)) !== null
  );
}
